import moment from 'moment';
import { ClientManagerInstanceToken } from '../../interfaces/client-manager.interface';
import { ConfigService } from '../../services/config/config.service';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { TimesheetAndExpensesUtilities } from '../../utilities/timesheet-and-expenses-utilities';
import { TimesheetModel } from '../low-level/timesheet.model';
import { DateModelOptions } from '../options/date-model-options';
import { RadioModelOptions } from '../options/radio-model-options';
import { TimeModelOptions } from '../options/time-model-options';
import { DateModel } from '../primitive/date.model';
import { TextModel } from '../primitive/text.model';
import { TimeModel } from '../primitive/time.model';
import { CompositeModel } from './composite.model';
import { RadioModel } from './radio.model';
import { getNextTimesheetEntryCounter } from './timesheet-entry-counter';
import { TimesheetEntryInterface } from './timesheet-entry.interface';

export class TimesheetEowModel extends CompositeModel implements TimesheetEntryInterface {
    public startDate: DateModel;
    public startTime: TimeModel;
    public endDate: DateModel;
    public endTime: TimeModel;
    public endAddressOptions: RadioModel<TextModel>;

    public readonly index: number;
    public constructor(
        time: moment.Moment,
        currentTimesheet: TimesheetModel,
        submittedTimesheet?: TimesheetModel,
        endAddress?: string,
    ) {
        super('TimesheetEoWModel');

        const injector = CustomInjector.getInjector();
        const configService = injector.get(ConfigService);
        const timesheetAndExpensesUtilities = injector.get(TimesheetAndExpensesUtilities);
        const clientManager = injector.get(ClientManagerInstanceToken);

        this.index = getNextTimesheetEntryCounter();

        // Only for P58 (NGMAA sets EoW according to work schedule and user cannot edit it):
        // Eow minValue should be the end date and time for the last entry in timesheet.
        // If an entry doesn't exist then minvalue is the end date and time of Sow.
        // If the timesheet is already submitted Eow minvalue is the submitted timesheet's eow value.
        let modelOptions = {};
        if (clientManager.isNgmP58()) {
            const entries = currentTimesheet.getTimesheetEntries();
            let previousEntry = moment.invalid();
            if (entries.length) {
                previousEntry = entries[entries.length - 1].endDate.value;
            } else if (currentTimesheet.startOfWork) {
                previousEntry = currentTimesheet.startOfWork;
            } else if (submittedTimesheet) {
                previousEntry = submittedTimesheet.getEndOfWork();
                // BUG: previousEntry can be null here if there is no previous timesheet. The default timesheet that will be
                //      initialized in that case will have null as EoW entry.
                time = previousEntry.clone();
            }

            const endTimeFlexibleHours = configService.getEowFlexibleHours();
            const maxEoWTime = time.clone().add(endTimeFlexibleHours, 'hours');

            modelOptions = { readonly: false, minValue: previousEntry, maxValue: maxEoWTime };
        }
        this.startDate = new DateModel('StartDate', time, new DateModelOptions(modelOptions));
        this.startTime = new TimeModel('StartTime', time, new TimeModelOptions(modelOptions));
        this.endDate = new DateModel('EndDate', time, new DateModelOptions(modelOptions));
        this.endTime = new TimeModel('EndTime', time, new TimeModelOptions(modelOptions));

        if (configService.getDisplayTimesheetAddressOptions()) {
            this.endAddressOptions = <RadioModel<TextModel>>this.set(
                new RadioModel<TextModel>(
                    'AddrNumber',
                    timesheetAndExpensesUtilities.getTimesheetAddressOptions(),
                    new RadioModelOptions({
                        initialSelection: 2,
                    }),
                ),
            );
            if (endAddress) {
                this.endAddressOptions.selectByPredicate((entry) => endAddress === entry.model.value);
            }
        }
    }
}
